import { useDispatch } from "react-redux";
import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { buildQueryParams } from "../utils/utils";
import { useAuthContext } from "./use-auth-context.hook";
import { setProducts } from "../../redux/vendor/products/productsActions";
import { Product } from "../../modules/vendor/my-products/models/product.model";
import { logger } from "../../utils/logger";
import {
    CatalogFacets,
    CatalogFilters,
    CatalogPageRequest,
    EMPTY_CATALOG_FACETS,
    catalogQueryString,
} from "../models/catalog-query.model";

export const useProductsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const dispatch = useDispatch()

    const refreshProductsList = async (
        draft: 'exclude' | 'only' | 'all' = 'exclude',
    ) => {
        dispatch(setProducts(await findAllProducts(undefined, undefined, draft)))
    }

    /**
     * Every default product of the vendor, fetched page by page.
     *
     * One request used to ask for page 1 at limit 1000 and stop there, so a
     * catalogue past 1000 products silently lost the rest in every screen that
     * lists products. Pass `page` to fetch that single page only.
     */
    const findAllProducts = async (
        page?: number,
        pageSize: number = 1000,
        draft: 'exclude' | 'only' | 'all' = 'exclude',
    ): Promise<any> => {
        const fetchPage = async (pageNumber: number): Promise<any> => sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.findAll + buildQueryParams([
                { key: "page", value: String(pageNumber) },
                { key: "limit", value: String(pageSize) },
                { key: "hidden", value: "true" },
                { key: "isDefault", value: "true" },
                { key: "draft", value: draft },
            ]),
        );

        const rows: any[] = [];
        if (page !== undefined) {
            rows.push(...(await fetchPage(page)).data);
        } else {
            // nextPage is -1 on the last page; the page cap only guards against a
            // response that never says so.
            for (let pageNumber = 1; pageNumber <= 1000; pageNumber++) {
                const response: any = await fetchPage(pageNumber);
                rows.push(...(response.data || []));
                if (!response.nextPage || response.nextPage < 0 || !response.data?.length) break;
            }
        }

        // Map each product through prepareProduct to handle V2 structure
        return rows.map((productData: any) => {
            const product = new Product();
            return product.prepareProduct(productData);
        })
    };

    /** One page of the catalogue, filtered, searched and sorted on the server. */
    const findProductsPage = async (
        request: CatalogPageRequest,
    ): Promise<{ products: Product[]; total: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.findAll + catalogQueryString(request),
        );
        return {
            products: (response?.data || []).map((productData: any) => new Product().prepareProduct(productData)),
            total: Number(response?.total) || 0,
        };
    };

    /**
     * Every product matching a catalogue query, page by page — for deliberate
     * whole-set actions only (export, manual reordering), never to render a list.
     */
    const findAllMatching = async (
        filters: CatalogFilters & { order?: string },
        pageSize = 500,
    ): Promise<Product[]> => {
        const all: Product[] = [];
        for (let page = 1; page <= 1000; page++) {
            const { products, total } = await findProductsPage({ ...filters, page, limit: pageSize });
            all.push(...products);
            if (products.length < pageSize || all.length >= total) break;
        }
        return all;
    };

    /** Family counts, filter values, appearance chips and the SKU total for the catalogue. */
    const getCatalogFacets = async (filters: CatalogFilters): Promise<CatalogFacets> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.catalogFacets + catalogQueryString(filters),
        );
        return { ...EMPTY_CATALOG_FACETS, ...(response || {}) };
    };

    const findOneProduct = async (id: string): Promise<any> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.findOne(id)
        )
        const product = new Product()
        return product.prepareProduct(response)
    };

    const findOneProductAndDelete = async (id: string): Promise<any> => {
        await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.products.findOne(id)
        )
    };

    const bulkDeleteProducts = async (productIds: string[], onProgress?: (status: any) => void): Promise<any> => {
        logger.debug(`[DEBUG API] Starting bulkDeleteProducts for ${productIds.length} products`);

        const data = new FormData();
        data.append('data', JSON.stringify({
            productGroupId: `bulk-delete-${Date.now()}`,
            operations: productIds.map(uuid => ({
                operation: 'delete',
                uuid,
            })),
        }));

        try {
            const batchResponse: any = await sendAuthGuardedRequest(
                ApiMethod.POST,
                routes.products.bulkUpdate,
                data,
                {
                    //@ts-ignore
                    maxBodyLength: Infinity,
                    timeout: 2 * 60 * 1000,
                }
            );

            if (!batchResponse.batchId) {
                throw new Error('Backend did not return batchId');
            }

            logger.debug(`[DEBUG API] Bulk delete batch submitted, batchId: ${batchResponse.batchId}`);

            const result = await pollBulkBatchStatus(batchResponse.batchId, onProgress);
            logger.debug(`[DEBUG API] Bulk delete completed`);
            return result;
        } catch (error) {
            logger.error(`[ERROR API] Error in bulkDeleteProducts:`, error);
            throw error;
        }
    };

    const bulkPatchProducts = async (data): Promise<any> => {
        await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.products.bulkPatch,
            data
        )
    };

    const findOneAndUpdate = async (id: string, data): Promise<any> => {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.products.patchOne(id),
            formData
        )
    };

    const bulkUpdateProducts = async (defaultProductId, differences, files, onProgress?: (status: any) => void, source: 'import' | 'edit' = 'edit'): Promise<any> => {
        logger.debug(`[DEBUG API] Starting bulkUpdateProducts, source: ${source}`);

        let data = new FormData();

        data.append('data', JSON.stringify(differences));
        data.append('source', source);

        if (defaultProductId) {
            data.append('defaultProductId', defaultProductId);
        }

        // Ensure files is an array
        if (!Array.isArray(files)) {
            logger.error("Files parameter is not an array:", files);
            files = [];
        }

        logger.log(`Processing ${files.length} files for upload`);

        files.forEach((customFile, index) => {
            const { file, metaType, productId } = customFile;

            if (!file) {
                logger.error(`File object at index ${index} is missing the 'file' property:`, customFile);
                return;
            }

            if (!(file instanceof File)) {
                logger.error(`The file at index ${index} is not a valid File object:`, file);
                return;
            }

            if (!metaType) {
                logger.error(`File object at index ${index} is missing the 'metaType' property:`, customFile);
                return;
            }

            if (!productId) {
                logger.error(`File object at index ${index} is missing the 'productId' property:`, customFile);
                return;
            }

            logger.log(`Appending file: ${file.name}, metaType: ${metaType}, productId: ${productId}`);

            if (metaType === 'original') {
                data.append('images', file, `image_${productId}.png`);
            } else if (metaType === 'thumbnail') {
                data.append('images', file, `thumbnail_${productId}.png`);
            } else if (metaType === 'plank') {
                data.append('images', file, file.name); // e.g., material_{plankGroupId}_{index}.png
            } else if (metaType === 'product_image') {
                data.append('images', file, file.name); // e.g., image_{uuid}.png (pre-named in buildAPIPayload)
            } else if (metaType === 'model3d') {
                data.append('images', file, file.name); // e.g., material_{uuid}.glb (pre-named in buildAPIPayload)
            } else {
                logger.error(`Unknown metaType '${metaType}' for file at index ${index}`);
                return;
            }
        });

        // Log FormData entries for debugging
        logger.debug("FormData entries:");
        for (let pair of data.entries()) {
            if (pair[0] === 'data') {
                logger.debug(`${pair[0]}: (JSON data)`);
            } else if (pair[0] === 'images') {
                // Check if it's a File object before accessing name property
                if (pair[1] instanceof File) {
                    logger.debug(`${pair[0]}: ${(pair[1] as File).name}`);
                } else {
                    logger.debug(`${pair[0]}: (file data)`);
                }
            } else {
                logger.debug(`${pair[0]}: ${pair[1]}`);
            }
        }

        try {
            logger.debug(`[DEBUG API] Submitting bulk update batch`);

            // Submit the batch - backend now always returns 202 with batchId (async only)
            const batchResponse: any = await sendAuthGuardedRequest(
                ApiMethod.POST,
                routes.products.bulkUpdate,
                data,
                {
                    //@ts-ignore
                    maxBodyLength: Infinity,
                    timeout: 2 * 60 * 1000,
                }
            );

            // Backend is now async-only, always returns batchId
            if (!batchResponse.batchId) {
                throw new Error('Backend did not return batchId - async job queue may not be configured');
            }

            logger.debug(`[DEBUG API] Batch submitted, batchId: ${batchResponse.batchId}`);

            // Poll for batch completion
            const result = await pollBulkBatchStatus(batchResponse.batchId, onProgress);

            logger.debug(`[DEBUG API] Batch completed`);
            return result;
        } catch (error) {
            logger.error(`[ERROR API] Error in bulkUpdateProducts:`, error);
            throw error;
        }
    };

    const generateSeamlessForPattern = async (body, pattern) => {
        logger.debug(`[DEBUG API] Starting generateSeamlessForPattern for pattern ${pattern}`);
        logger.debug(`[DEBUG API] Body contains ${body.files?.length || 0} files`);

        let data = new FormData();
        if (body.plankWidth)
            data.append('plankWidth', body.plankWidth);
        if (body.plankHeight)
            data.append('plankHeight', body.plankHeight);
        if (body.textureWidth)
            data.append('textureWidth', body.textureWidth);
        if (body.textureHeight)
            data.append('textureHeight', body.textureHeight);
        if (body.pattern)
            data.append('pattern', body.pattern);
        if (body.octoImageHeight)
            data.append('octoImageHeight', body.octoImageHeight);
        if (body.squareImageHeight)
            data.append('squareImageHeight', body.squareImageHeight);
        if (body.groutLineWidth)
            data.append('groutLineWidth', body.groutLineWidth);
        if (body.groutLineColor)
            data.append('groutLineColor', body.groutLineColor);
        if (body.lengthL1)
            data.append('lengthL1', body.lengthL1);
        if (body.lengthL2)
            data.append('lengthL2', body.lengthL2);
        if (body.lengthL3)
            data.append('lengthL3', body.lengthL3);
        if (body.offset)
            data.append('offset', body.offset);
        if (body.randomize !== undefined)
            data.append('randomize', body.randomize.toString());
        if (body.mirror !== undefined)
            data.append('mirror', body.mirror.toString());

        logger.debug(`[DEBUG API] FormData initialized with parameters`);

        let filePromises = []
        let octoPromises = []
        let squarePromises = []
        if (body.files && body.files.length > 0) {
            logger.debug(`[DEBUG API] Processing ${body.files.length} files for pattern ${pattern}`);
            filePromises = body.files.map((file, index) => {
                return new Promise((resolve, reject) => {
                    try {
                        logger.debug(`[DEBUG API] Processing file ${index}:`, file.name);
                        const reader = new FileReader();
                        reader.readAsArrayBuffer(file);
                        reader.onload = () => {
                            try {
                                const blob = new Blob([reader.result], { type: file.type });
                                data.append("files", blob, "file" + index + ".jpg");
                                logger.debug(`[DEBUG API] Added file ${index} to FormData`);
                                resolve(true);
                            } catch (blobError) {
                                logger.error(`[ERROR API] Failed to create blob for file ${index}:`, blobError);
                                reject(blobError);
                            }
                        };
                        reader.onerror = (error) => {
                            logger.error(`[ERROR API] FileReader error for file ${index}:`, error);
                            reject(error);
                        };
                    } catch (fileError) {
                        logger.error(`[ERROR API] Exception handling file ${index}:`, fileError);
                        reject(fileError);
                    }
                });
            });
        } else {
            logger.error(`[ERROR API] No files provided for pattern ${pattern}`);
        }

        if (body.imageA) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageA);
                reader.onload = () => {
                    const blob = new Blob([reader.result], { type: body.imageA.type });
                    data.append("imageA", blob, body.imageA.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }))

        }

        if (body.imageB) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageB);
                reader.onload = () => {
                    const blob = new Blob([reader.result], { type: body.imageB.type });
                    data.append("imageB", blob, body.imageB.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }))
        }

        if (body.imageC) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageC);
                reader.onload = () => {
                    const blob = new Blob([reader.result], { type: body.imageC.type });
                    data.append("imageC", blob, body.imageC.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }))
        }
        if (body.imageD) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageD);
                reader.onload = () => {
                    const blob = new Blob([reader.result], { type: body.imageD.type });
                    data.append("imageD", blob, body.imageD.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }))
        }

        if (body.octoImages)
            octoPromises = body.octoImages.map((file, index) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(file);
                    reader.onload = () => {
                        const blob = new Blob([reader.result], { type: file.type });
                        data.append("octoImages", blob, "file_ocot_" + index + ".jpg");
                        resolve(true);
                    };
                    reader.onerror = reject;
                });
            });

        if (body.squareImages)
            squarePromises = body.squareImages.map((file, index) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(file);
                    reader.onload = () => {
                        const blob = new Blob([reader.result], { type: file.type });
                        data.append("squareImages", blob, "file_square_" + index + ".jpg");
                        resolve(true);
                    };
                    reader.onerror = reject;
                });
            });

        try {
            logger.debug(`[DEBUG API] Waiting for all file promises to resolve`);
            await Promise.all(filePromises);
            await Promise.all(octoPromises);
            await Promise.all(squarePromises);

            logger.debug(`[DEBUG API] All files processed, submitting job for pattern ${pattern}`);

            // Submit the job and get jobId
            const requestUrl = routes.products.generateSeamlessForPattern(pattern);
            logger.debug(`[DEBUG API] Request URL: ${requestUrl}`);

            const jobResponse: any = await sendAuthGuardedRequest(
                ApiMethod.POST,
                requestUrl,
                data,
                {
                    //@ts-ignore
                    maxBodyLength: Infinity,
                    timeout: 2 * 60 * 1000,
                }
            );

            logger.debug(`[DEBUG API] Job submitted for pattern ${pattern}, jobId: ${jobResponse.jobId}`);

            // Poll for job completion
            const result = await pollSeamlessJobStatus(jobResponse.jobId);

            logger.debug(`[DEBUG API] Job completed for pattern ${pattern}`);
            return result;
        } catch (error) {
            logger.error(`[ERROR API] Error in generateSeamlessForPattern for pattern ${pattern}:`, error);
            throw error;
        }
    }

    const pollSeamlessJobStatus = async (jobId: string, maxAttempts: number = 60): Promise<{ image: string, textureWidth?: number, textureHeight?: number }> => {
        logger.debug(`[DEBUG API] Starting to poll job status for jobId: ${jobId}`);

        let attempts = 0;
        const pollInterval = 2000; // 2 seconds

        while (attempts < maxAttempts) {
            try {
                const statusResponse: any = await sendAuthGuardedRequest(
                    ApiMethod.GET,
                    routes.products.seamlessJobStatus(jobId)
                );

                logger.debug(`[DEBUG API] Job status for ${jobId}: ${statusResponse.status}, attempt ${attempts + 1}`);

                if (statusResponse.status === 'completed') {
                    logger.debug(`[DEBUG API] Job ${jobId} completed, downloading result from: ${statusResponse.resultUrl}`);

                    // Download the result image from the resultUrl
                    const imageResponse = await fetch(statusResponse.resultUrl);
                    const arrayBuffer = await imageResponse.arrayBuffer();

                    // Convert to base64
                    const uint8Array = new Uint8Array(arrayBuffer);
                    let base64String = '';
                    const chunkSize = 0x8000; // 32 KB chunks
                    for (let i = 0; i < uint8Array.length; i += chunkSize) {
                        base64String += String.fromCharCode.apply(null, Array.from(uint8Array.subarray(i, i + chunkSize)));
                    }

                    const result = btoa(base64String);
                    logger.debug(`[DEBUG API] Successfully downloaded and converted result for job ${jobId}, dimensions: ${statusResponse.textureWidth}x${statusResponse.textureHeight}m`);
                    return {
                        image: result,
                        textureWidth: statusResponse.textureWidth,
                        textureHeight: statusResponse.textureHeight,
                    };
                } else if (statusResponse.status === 'failed') {
                    logger.error(`[ERROR API] Job ${jobId} failed: ${statusResponse.error}`);
                    throw new Error(`Seamless generation failed: ${statusResponse.error}`);
                }

                // Still pending or processing, wait before next attempt
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                attempts++;
            } catch (error) {
                logger.error(`[ERROR API] Error polling job status for ${jobId}:`, error);
                throw error;
            }
        }

        throw new Error(`Job ${jobId} timed out after ${maxAttempts * pollInterval / 1000} seconds`);
    }

    const getSeamlessJobStatus = async (jobId: string): Promise<any> => {
        logger.debug(`[DEBUG API] Getting status for job ${jobId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.seamlessJobStatus(jobId)
        );
        return response;
    }

    const getSeamlessJobs = async (status?: string, limit?: number): Promise<any> => {
        logger.debug(`[DEBUG API] Getting seamless jobs, status: ${status}, limit: ${limit}`);
        const queryString = buildQueryParams([
            { key: "status", value: status },
            { key: "limit", value: limit?.toString() }
        ]);
        const response = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.seamlessJobs + queryString
        );
        return response;
    }

    const retrySeamlessJob = async (jobId: string): Promise<any> => {
        logger.debug(`[DEBUG API] Retrying job ${jobId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.products.seamlessRetry(jobId)
        );
        return response;
    }

    const pollBulkBatchStatus = async (batchId: string, onProgress?: (status: any) => void, maxAttempts: number = 300): Promise<any> => {
        logger.debug(`[DEBUG API] Starting to poll bulk batch status for batchId: ${batchId}`);

        let attempts = 0;
        const pollInterval = 2000; // 2 seconds

        while (attempts < maxAttempts) {
            try {
                const statusResponse: any = await sendAuthGuardedRequest(
                    ApiMethod.GET,
                    routes.products.bulkStatus(batchId)
                );

                logger.debug(`[DEBUG API] Batch status for ${batchId}: ${JSON.stringify(statusResponse.summary)}, attempt ${attempts + 1}`);

                // Call progress callback if provided
                if (onProgress) {
                    onProgress(statusResponse);
                }

                // Check if batch is complete (no pending, generating_seamless, or processing jobs)
                const inProgress = statusResponse.summary.pending + (statusResponse.summary.generating_seamless || 0) + statusResponse.summary.processing;

                if (inProgress === 0) {
                    logger.debug(`[DEBUG API] Batch ${batchId} completed`);
                    return statusResponse;
                }

                // Still processing, wait before next attempt
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                attempts++;
            } catch (error) {
                logger.error(`[ERROR API] Error polling bulk batch status for ${batchId}:`, error);
                throw error;
            }
        }

        throw new Error(`Batch ${batchId} timed out after ${maxAttempts * pollInterval / 1000} seconds`);
    }

    const getBulkBatchStatus = async (batchId: string): Promise<any> => {
        logger.debug(`[DEBUG API] Getting status for batch ${batchId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.bulkStatus(batchId)
        );
        return response;
    }

    const retryBulkJob = async (jobId: string, correctedData?: any): Promise<any> => {
        logger.debug(`[DEBUG API] Retrying bulk job ${jobId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.products.bulkRetry(jobId),
            correctedData ? { correctedData } : undefined
        );
        return response;
    }

    const updateCustomFieldFilters = async (): Promise<any> => {
        logger.log('Triggering instant custom field filter update');
        const response = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.products.updateCustomFieldFilters,
        );
        logger.log('Custom field filters updated successfully');
        return response;
    };

    const abortBulkBatch = async (batchId: string): Promise<any> => {
        logger.debug(`[DEBUG API] Aborting bulk batch ${batchId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.products.bulkAbort(batchId)
        );
        return response;
    };

    const getBatchHistory = async (hours: number = 168, limit: number = 20, skip: number = 0, status?: string, search?: string): Promise<any> => {
        logger.debug(`[DEBUG API] Getting batch history, hours: ${hours}, limit: ${limit}, skip: ${skip}, status: ${status}, search: ${search}`);
        const params = [
            { key: "hours", value: hours.toString() },
            { key: "limit", value: limit.toString() },
            { key: "skip", value: skip.toString() }
        ];
        if (status && status !== 'all') {
            params.push({ key: "status", value: status });
        }
        if (search && search.trim()) {
            params.push({ key: "search", value: search.trim() });
        }
        const queryString = buildQueryParams(params);
        const response = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.batchHistory + queryString
        );
        return response;
    };

    /**
     * Creates a multi-batch import session
     */
    const createBulkSession = async (totalBatches: number, totalProducts: number): Promise<any> => {
        logger.debug(`[DEBUG API] Creating bulk session, totalBatches: ${totalBatches}, totalProducts: ${totalProducts}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.products.bulkSession,
            { totalBatches, totalProducts }
        );
        return response;
    };

    /**
     * Gets the status of a multi-batch import session
     */
    const getBulkSessionStatus = async (sessionId: string): Promise<any> => {
        logger.debug(`[DEBUG API] Getting bulk session status for ${sessionId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.products.bulkSessionStatus(sessionId)
        );
        return response;
    };

    return {
        refreshProductsList,
        findAllProducts,
        findProductsPage,
        findAllMatching,
        getCatalogFacets,
        findOneProduct,
        findOneProductAndDelete,
        bulkDeleteProducts,
        findOneAndUpdate,
        bulkPatchProducts,
        bulkUpdateProducts,
        generateSeamlessForPattern,
        pollSeamlessJobStatus,
        getSeamlessJobStatus,
        getSeamlessJobs,
        retrySeamlessJob,
        pollBulkBatchStatus,
        getBulkBatchStatus,
        retryBulkJob,
        abortBulkBatch,
        updateCustomFieldFilters,
        getBatchHistory,
        createBulkSession,
        getBulkSessionStatus
    };
};
